import { MdDelete, MdEdit } from 'react-icons/md';

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Select from '@/components/Select';
import useFetchOrganization from '@/features/agent/organizations/api/useFetchOrganization';
import useUpdateOrganization from '@/features/agent/organizations/api/useUpdateOrganization';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import useFetchOrganizationCandidateProfilesCustomFields from '@/features/agent/organizations/api/useFetchOrganizationCandidateProfilesCustomFields';
import CandidateMailSignatureDeleteModal from './CandidateMailSignatureDeleteModal';

const UNSET_CUSTOM_FIELD_VALUE = '__unset__';
// OrganizationSetting::MAIL_SENDER_MAX_LENGTH と一致
const SENDER_MAX_LENGTH = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CandidateMailSettings() {
  const { data: organizationData, isLoading, isError } = useFetchOrganization();
  const { mutateAsync: updateOrganization, isPending: isSaving } = useUpdateOrganization();
  const queryClient = useQueryClient();
  const invalidateMailSettings = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organization'] }),
      queryClient.invalidateQueries({ queryKey: ['candidateMailSetting'] }),
    ]);
  const notify = (message: string, type: 'success' | 'error') =>
    type === 'success' ? showSuccessToast(message) : showErrorToast(message);
  const {
    data: candidateCustomFields,
    isLoading: isLoadingCustomFields,
    isError: isErrorCustomFields,
  } = useFetchOrganizationCandidateProfilesCustomFields();

  const mailSettings = organizationData?.mail_settings ?? null;
  const [isEditing, setIsEditing] = useState(false);
  const [senderValue, setSenderValue] = useState<string>('');
  const [replyAddressValue, setReplyAddressValue] = useState<string>('');
  const [replyAddressError, setReplyAddressError] = useState<string | null>(null);
  const [candidateFieldValue, setCandidateFieldValue] = useState<string>(UNSET_CUSTOM_FIELD_VALUE);
  const [isEditingSignature, setIsEditingSignature] = useState(false);
  const [signatureValue, setSignatureValue] = useState<string>('');
  const [isSignatureDeleteModalOpen, setIsSignatureDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (isEditing) return;
    setSenderValue(mailSettings?.sender ?? '');
    setReplyAddressValue(mailSettings?.reply_address ?? '');
  }, [mailSettings?.sender, mailSettings?.reply_address, isEditing]);

  useEffect(() => {
    if (isEditingSignature) return;
    setSignatureValue(mailSettings?.signature ?? '');
  }, [mailSettings?.signature, isEditingSignature]);

  useEffect(() => {
    const storedValue = mailSettings?.candidate_address_custom_field_id;
    if (!storedValue) {
      setCandidateFieldValue(UNSET_CUSTOM_FIELD_VALUE);
      return;
    }

    const fields = candidateCustomFields ?? [];
    const matchedById = fields.find((field) => String(field.id) === storedValue);
    if (matchedById) {
      setCandidateFieldValue(String(matchedById.id));
      return;
    }

    const matchedByAlias = fields.find((field) => field.alias_id === storedValue);
    if (matchedByAlias) {
      setCandidateFieldValue(String(matchedByAlias.id));
      return;
    }

    setCandidateFieldValue(UNSET_CUSTOM_FIELD_VALUE);
  }, [mailSettings?.candidate_address_custom_field_id, candidateCustomFields]);

  const latestSender = mailSettings?.sender ?? '';
  const latestReplyAddress = mailSettings?.reply_address ?? '';
  const latestSignature = mailSettings?.signature ?? '';

  const sanitizeOptionalInput = (value: string) => {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  // mail_settings は全置換のため、更新時は常に全項目を送る
  const buildMailSettings = (
    overrides?: Partial<{
      sender: string | null;
      reply_address: string | null;
      candidate_address_custom_field_id: string | null;
      signature: string | null;
    }>
  ) => ({
    sender: sanitizeOptionalInput(senderValue),
    reply_address: sanitizeOptionalInput(replyAddressValue),
    candidate_address_custom_field_id:
      candidateFieldValue === UNSET_CUSTOM_FIELD_VALUE ? null : candidateFieldValue,
    signature: sanitizeOptionalInput(signatureValue),
    ...overrides,
  });

  // mail_settings は全置換で保存するため、読み込み前に編集を始めると未取得の項目を空で上書きしてしまう
  const isSettingsNotReady = isLoading || isLoadingCustomFields || !organizationData;

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setSenderValue(latestSender);
    setReplyAddressValue(latestReplyAddress);
    setReplyAddressError(null);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    const trimmedReplyAddress = replyAddressValue.trim();
    if (trimmedReplyAddress.length > 0 && !EMAIL_PATTERN.test(trimmedReplyAddress)) {
      setReplyAddressError('メールアドレスの形式が不正です');
      return;
    }
    setReplyAddressError(null);

    try {
      await updateOrganization({ mail_settings: buildMailSettings() });
      await invalidateMailSettings();
      notify('差出人設定を保存しました', 'success');
      setIsEditing(false);
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : '差出人設定の保存に失敗しました';
      notify(message, 'error');
    }
  };

  const handleStartEditSignature = () => {
    setIsEditingSignature(true);
  };

  const handleCancelEditSignature = () => {
    setSignatureValue(latestSignature);
    setIsEditingSignature(false);
  };

  const handleSaveSignature = async () => {
    const sanitizedSignature = sanitizeOptionalInput(signatureValue);
    try {
      await updateOrganization({
        mail_settings: buildMailSettings({ signature: sanitizedSignature }),
      });
      await invalidateMailSettings();
      notify('署名設定を保存しました', 'success');
      setIsEditingSignature(false);
    } catch (error) {
      notify('署名設定の保存に失敗しました', 'error');
    }
  };

  const handleDeleteSignature = async () => {
    try {
      await updateOrganization({ mail_settings: buildMailSettings({ signature: null }) });
      await invalidateMailSettings();
      setSignatureValue('');
      setIsEditingSignature(false);
      setIsSignatureDeleteModalOpen(false);
      notify('署名設定を削除しました', 'success');
    } catch (error) {
      notify('署名設定の削除に失敗しました', 'error');
    }
  };

  const handleCandidateFieldChange = async (value: string) => {
    const previousValue = candidateFieldValue;
    setCandidateFieldValue(value);

    try {
      await updateOrganization({
        mail_settings: buildMailSettings({
          candidate_address_custom_field_id: value === UNSET_CUSTOM_FIELD_VALUE ? null : value,
        }),
      });
      await invalidateMailSettings();
      notify('宛先項目を保存しました', 'success');
    } catch (error) {
      setCandidateFieldValue(previousValue);
      notify('宛先項目の保存に失敗しました', 'error');
    }
  };

  const resolveDisplayText = (value: string | null | undefined) => {
    if (isLoading) return '読み込み中...';
    if (isError) return '取得に失敗しました';
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : '未設定';
  };

  const senderName = resolveDisplayText(senderValue);
  const replyAddress = resolveDisplayText(replyAddressValue);
  const senderClassName = senderName === '未設定' ? 'text-disabled' : 'text-main';
  const replyClassName = replyAddress === '未設定' ? 'text-disabled' : 'text-main';

  const hasSignature = latestSignature.trim().length > 0;

  const candidateFieldOptions = useMemo(() => {
    const options =
      candidateCustomFields?.map((field) => ({
        value: String(field.id),
        label: field.name,
      })) ?? [];

    return [{ value: UNSET_CUSTOM_FIELD_VALUE, label: '未設定' }, ...options];
  }, [candidateCustomFields]);

  const selectDefaultLabel = isLoadingCustomFields
    ? '読み込み中...'
    : isErrorCustomFields
      ? '取得に失敗しました'
      : '未設定';

  return (
    <div className="flex w-full flex-col">
      <section className="flex flex-col gap-md border-b border-solid border-border p-lg">
        <div className="flex items-start justify-between gap-md">
          <div className="flex min-w-0 flex-1 flex-col gap-md">
            <header className="flex min-h-8 items-center gap-xs">
              <h2 className="text-heading-xs text-main">差出人設定</h2>
              <span className="text-body-xs text-sub">(受信トレイの差出人として表示されます)</span>
            </header>
            <div className="flex flex-col gap-md">
              <div className="flex flex-col gap-xxs">
                <span className="text-heading-xxs text-main">名前</span>
                {isEditing ? (
                  <>
                    <Input
                      className="w-full max-w-[640px]"
                      value={senderValue}
                      onChange={(event) => setSenderValue(event.target.value)}
                      maxLength={SENDER_MAX_LENGTH}
                      placeholder="テキスト"
                    />
                    <span className="text-body-xs text-sub">
                      {senderValue.length}/{SENDER_MAX_LENGTH}文字
                    </span>
                  </>
                ) : (
                  <span className={`text-body-sm ${senderClassName}`}>{senderName}</span>
                )}
              </div>
              <div className="flex flex-col gap-xxs">
                <span className="text-heading-xxs text-main">返信先</span>
                {isEditing ? (
                  <>
                    <Input
                      className="w-full max-w-[640px]"
                      value={replyAddressValue}
                      onChange={(event) => {
                        setReplyAddressValue(event.target.value);
                        setReplyAddressError(null);
                      }}
                      placeholder=""
                      type="email"
                      error={!!replyAddressError}
                    />
                    {replyAddressError && (
                      <span role="alert" className="text-body-xs text-danger">
                        {replyAddressError}
                      </span>
                    )}
                  </>
                ) : (
                  <span className={`text-body-sm ${replyClassName}`}>{replyAddress}</span>
                )}
              </div>
            </div>
          </div>
          {isEditing ? (
            <div className="flex shrink-0 gap-xs">
              <Button
                color="secondary"
                size="sm"
                type="button"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                キャンセル
              </Button>
              <Button
                color="primary"
                size="sm"
                type="button"
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                保存
              </Button>
            </div>
          ) : (
            <Button
              color="secondary"
              size="sm"
              className="shrink-0"
              type="button"
              onClick={handleStartEdit}
              disabled={isSettingsNotReady}
            >
              <MdEdit size={16} />
              <span>編集</span>
            </Button>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-md border-b border-solid border-border p-lg">
        <div className="flex items-start justify-between gap-md">
          <div className="flex min-w-0 flex-1 flex-col gap-md">
            <header className="flex min-h-8 items-center gap-xs">
              <h2 className="text-heading-xs text-main">署名設定</h2>
              <span className="text-body-xs text-sub">(送信メールの最後に追加されます)</span>
            </header>
            {isEditingSignature ? (
              <textarea
                className="min-h-[200px] w-full resize-y rounded-sm border border-solid border-border bg-white px-sm py-xs text-body-sm text-main outline-hidden focus:border-primary"
                value={signatureValue}
                onChange={(event) => setSignatureValue(event.target.value)}
                placeholder="署名を入力してください"
              />
            ) : hasSignature ? (
              <div className="text-body-sm whitespace-pre-line text-main">{signatureValue}</div>
            ) : (
              <span className="text-body-sm text-disabled">未設定</span>
            )}
          </div>
          {isEditingSignature ? (
            <div className="flex shrink-0 gap-xs">
              <Button
                color="secondary"
                size="sm"
                type="button"
                onClick={handleCancelEditSignature}
                disabled={isSaving}
              >
                キャンセル
              </Button>
              <Button
                color="primary"
                size="sm"
                type="button"
                onClick={handleSaveSignature}
                disabled={isSaving}
              >
                保存
              </Button>
            </div>
          ) : (
            <div className="flex shrink-0 gap-xs">
              {hasSignature && (
                <Button
                  color="danger-secondary"
                  size="sm"
                  type="button"
                  onClick={() => setIsSignatureDeleteModalOpen(true)}
                  disabled={isSaving}
                >
                  <MdDelete size={16} />
                  <span>削除</span>
                </Button>
              )}
              <Button
                color="secondary"
                size="sm"
                type="button"
                onClick={handleStartEditSignature}
                disabled={isSettingsNotReady}
              >
                <MdEdit size={16} />
                <span>編集</span>
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-md p-lg">
        <header className="flex min-h-8 items-center gap-xs">
          <h2 className="text-heading-xs text-main">送信先設定</h2>
        </header>
        <div className="flex flex-col gap-xs">
          <span className="text-heading-xxs text-main">求職者のメールアドレス項目</span>
          <div className="w-[236px]">
            <Select
              defaultLabel={selectDefaultLabel}
              items={candidateFieldOptions}
              className="w-full"
              id="candidate-mail-contact"
              value={candidateFieldValue}
              onValueChange={handleCandidateFieldChange}
              disabled={isSaving || isLoadingCustomFields || isErrorCustomFields}
            />
          </div>
        </div>
      </section>

      <CandidateMailSignatureDeleteModal
        open={isSignatureDeleteModalOpen}
        onClose={() => setIsSignatureDeleteModalOpen(false)}
        onConfirm={handleDeleteSignature}
        isDeleting={isSaving}
      />
    </div>
  );
}
